import { askedHow } from "./askedHow";
import {
  getGradeLabel,
  getGradePresetWorksheetCount,
  getLibraryWorksheetTotal,
  GRADE_2_REBUILDING_TEXT,
  WORKSHEET_ACTIVITY_LABELS,
  WORKSHEET_GRADE_ACTIVITIES,
} from "../../../pages/learning-center/data/catalog";
import { RESOURCES, worksheetsHref } from "../../../routes";
import { librarySections, type LibraryUser } from "../../../utils/libraryNav";
import { askFor } from "./findByName";
import { openPageEntry } from "./openPage";
import { defineEntry, isSlotQuestion } from "./types";
import { listOf } from "./wording";

const showsWorksheets = (user: LibraryUser) => librarySections(user).worksheets;
const showsResources = (user: LibraryUser) => librarySections(user).resources;

/** "grade 1", "Grade R", "gr 2" */
const GRADE = /\b(?:grade|gr)\s*(r|1|2|one|two)\b/i;
const GRADE_KEYS: Record<string, string> = { r: "grade-r", "1": "grade-1", one: "grade-1", "2": "grade-2", two: "grade-2" };
/** "grade 3" up to "grade 12": a grade the Library has no worksheets for. */
const OTHER_GRADE = /\b(?:grade|gr)\s*([3-9]|1[0-2])\b/i;

/** The words for each kind of worksheet, including how its card names it. */
const ACTIVITY_WORDS: Record<string, RegExp> = {
  coloring: /\bcolou?r(?:ing|s)?\b/i,
  abc: /\b(?:abc|alphabet|letters?)\b/i,
  tracing: /\b(?:words?|tracing|phonics|spelling)\b/i,
  poems: /\b(?:poems?|poetry|rhymes?)\b/i,
  counting: /\b(?:counting|count|numbers?)\b/i,
  math: /\b(?:maths?|mathematics|sums|numeracy)\b/i,
  literacy: /\b(?:literacy|reading|writing|english)\b/i,
  "robotics-and-coding": /\b(?:robotics|coding|code)\b/i,
  "life-skills": /\blife[- ]?skills\b/i,
};

const activityLabel = (activity: string) => WORKSHEET_ACTIVITY_LABELS[activity];

/** What a worksheets question is about, as the Library's own pages split it. */
type Worksheets =
  | { kind: "all"; total: number }
  | { kind: "rebuilding" }
  | { kind: "otherGrade"; grade: string; grades: string[] }
  | { kind: "grade"; gradeKey: string; grade: string; total: number; activities: string[]; missing?: string }
  | { kind: "activity"; gradeKey: string; grade: string; activity: string; activityKey: string };

/** The grade and kind of worksheet a question names, if any, as the Library's pages offer them. */
function findWorksheets(said: string) {
  const gradeKey = GRADE_KEYS[said.match(GRADE)?.[1].toLowerCase() ?? ""];
  const activityKey = Object.keys(ACTIVITY_WORDS).find((key) => ACTIVITY_WORDS[key].test(said));
  const total = getLibraryWorksheetTotal();
  const other = said.match(OTHER_GRADE)?.[1];
  if (!gradeKey && other) {
    const grades = Object.keys(WORKSHEET_GRADE_ACTIVITIES).map(getGradeLabel);
    return { kind: "otherGrade", grade: `Grade ${other}`, grades } as const;
  }
  if (!gradeKey && !activityKey) return { kind: "all", total } as const;
  if (gradeKey === "grade-2") return { kind: "rebuilding" } as const;

  const gradesWith = Object.keys(WORKSHEET_GRADE_ACTIVITIES).filter(
    (key) => !activityKey || WORKSHEET_GRADE_ACTIVITIES[key].includes(activityKey)
  );
  if (!gradeKey) {
    if (gradesWith.length === 0) return { kind: "all", total } as const;
    if (gradesWith.length > 1) {
      return askFor("worksheet", "ambiguous", "Which grade would you like?", gradesWith.map(getGradeLabel));
    }
  }
  const key = gradeKey ?? gradesWith[0];
  const grade = getGradeLabel(key);
  const activities = WORKSHEET_GRADE_ACTIVITIES[key];
  if (activityKey && activities.includes(activityKey)) {
    return { kind: "activity", gradeKey: key, grade, activityKey, activity: activityLabel(activityKey) } as const;
  }
  return {
    kind: "grade",
    gradeKey: key,
    grade,
    total: getGradePresetWorksheetCount(key),
    activities: activities.map(activityLabel),
    ...(activityKey ? { missing: activityLabel(activityKey) } : {}),
  } as const;
}

/** One way of wording each kind of worksheets answer. */
type WorksheetsWording = {
  all: (total: number) => string;
  grade: (grade: string, total: number, kinds: string) => string;
  activity: (grade: string, activity: string) => string;
};

const wordWorksheets = (w: Worksheets, wording: WorksheetsWording) => {
  switch (w.kind) {
    case "all":
      return wording.all(w.total);
    case "rebuilding":
      return GRADE_2_REBUILDING_TEXT;
    case "otherGrade":
      return `The Library has no ${w.grade} worksheets yet. It has worksheets for ${listOf(w.grades)}.`;
    case "activity":
      return wording.activity(w.grade, w.activity);
    case "grade": {
      const grade = wording.grade(w.grade, w.total, listOf(w.activities));
      return w.missing ? `${w.grade} doesn't have ${w.missing} worksheets. ${grade}` : grade;
    }
  }
};

export const libraryEntries = [
  openPageEntry("library", "It's where you find worksheets and resources.", [
    "Where can I find teaching materials?",
    "Where are the printables?",
  ]),

  // Worksheets, as Library and its grade pages count and list them
  defineEntry<{ worksheets: Worksheets }>({
    id: "library.worksheets",
    sidebarItem: "library",
    visibleTo: showsWorksheets,
    examples: [
      "Do we have any worksheets?",
      "Where do I find worksheets for grade 4?",
      "What's in the Library?",
      "What's in the libary?",
      "Is there anything for maths?",
      "How many worksheets are there?",
      "Where can I find worksheets?",
      "Find a worksheet",
      "Find a counting worksheet",
      "Find a colouring worksheet",
      "What worksheets are there for Grade 1?",
      "Show me the Grade R worksheets",
      "Is there a maths worksheet?",
      "I need a reading worksheet",
      "Worksheets for my class",
      "Printable worksheets for Grade R",
      "Alphabet worksheets",
      "Do we have phonics worksheets?",
      "Find maths worksheets in the library",
      "Find Grade 1 worksheets in the Library",
      "Which worksheets are there for Grade 3?",
      "Are there worksheets for Grade 5?",
      ...askedHow("find a worksheet for Grade 2"),
    ],
    slots: { worksheet: "" },
    answer: {
      load: async ({ worksheet: said = "" }) => {
        const worksheets = findWorksheets(said);
        return isSlotQuestion(worksheets) ? worksheets : { worksheets };
      },
      templates: [
        ({ worksheets }) =>
          wordWorksheets(worksheets, {
            all: (total) => `The Library has ${total} ready-made worksheets across Grade R and Grade 1.`,
            grade: (grade, total, kinds) => `${grade} has ${total} ready-made worksheets: ${kinds}.`,
            activity: (grade, activity) => `Here are the ${grade} ${activity} worksheets, ready to print.`,
          }),
        ({ worksheets }) =>
          wordWorksheets(worksheets, {
            all: (total) => `There are ${total} ready-made worksheets in the Library, for Grade R and Grade 1.`,
            grade: (grade, total, kinds) => `There are ${total} ready-made worksheets for ${grade}, in ${kinds}.`,
            activity: (grade, activity) => `You'll find the ${grade} ${activity} worksheets in the Library.`,
          }),
        ({ worksheets }) =>
          wordWorksheets(worksheets, {
            all: (total) => `You have ${total} ready-made worksheets in the Library, across Grade R and Grade 1.`,
            grade: (grade, total, kinds) => `For ${grade}, the Library has ${total} ready-made worksheets: ${kinds}.`,
            activity: (grade, activity) => `The ${grade} ${activity} worksheets are ready in the Library.`,
          }),
      ],
      link: (_, { worksheets: w }) =>
        w.kind === "activity"
          ? { label: `Open ${w.grade} ${w.activity}`, href: worksheetsHref(w.gradeKey, w.activityKey) }
          : w.kind === "grade"
            ? { label: `Open ${w.grade} worksheets`, href: worksheetsHref(w.gradeKey) }
            : { label: "Open worksheets", href: worksheetsHref() },
    },
  }),

  // Resources, for the Organizations whose Library shows it
  defineEntry({
    id: "library.resources",
    sidebarItem: "library",
    visibleTo: showsResources,
    examples: [
      "Where are the Sunday School resources?",
      "Where are the resources?",
      "Find a Bible story",
      "Do we have Sunday School stories?",
      "Where are the printable books?",
      "Find a book for Sunday School",
      "Resources for Sunday School",
      "Open resources",
      "Where can I download stories?",
      "Show me the Sunday School books",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Sunday School stories and printable books are in Resources. You can open, preview and download them.",
        () => "You'll find Sunday School stories and printable books in Resources, ready to preview and download.",
        () => "Resources has Sunday School stories and printable books for Sunday School and your community.",
      ],
      link: () => ({ label: "Open Resources", href: RESOURCES }),
    },
  }),
];
